import { useQuery } from '@tanstack/react-query'

import { useApi } from 'contexts/api-context'

import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import type { Customer, CustomerYouTubeAccount, UseQueryOmissions, YoutubeStatusType } from 'types'

export const useGetCustomers = (
  props?: Omit<UseQueryOptions<Customer[], Error>, UseQueryOmissions>
): UseQueryResult<Customer[], Error> => {
  const { enabled, ...rest } = props ?? {}
  const api = useApi()
  return useQuery<Customer[], Error>({
    staleTime: Infinity,
    queryKey: ['customers'],
    queryFn: () => api.get('/customers'),
    enabled: enabled !== false,
    ...rest,
  })
}

export const useGetCustomer = (
  customerGuid?: string,
  props?: Omit<UseQueryOptions<Customer, Error>, UseQueryOmissions>
): UseQueryResult<Customer, Error> => {
  const { enabled, ...rest } = props ?? {}
  const api = useApi()
  return useQuery<Customer, Error>({
    staleTime: Infinity,
    queryKey: ['customer', { customerGuid }],
    queryFn: () => api.get(`/customers/${customerGuid}`),
    enabled: !!customerGuid && enabled !== false,
    ...rest,
  })
}

export const useGetCustomerYoutubeAccount = (
  customerGuid?: string,
  props?: Omit<UseQueryOptions<CustomerYouTubeAccount, Error>, UseQueryOmissions>
): UseQueryResult<CustomerYouTubeAccount, Error> => {
  const api = useApi()
  return useQuery<CustomerYouTubeAccount, Error>({
    queryKey: ['customerYoutubeAccount', { customerGuid }],
    queryFn: async () => {
      try {
        const response = await api.get(`/customers/${customerGuid}/youtubeaccount`)
        return response.youTubeAccountinfo
      } catch (err) {
        return null
      }
    },
    enabled: !!customerGuid,
    staleTime: 3600000, // 1 hour
    ...props,
  })
}

export const useGetCustomerYoutubeStatus = (
  customerGuid?: string,
  props?: Omit<UseQueryOptions<YoutubeStatusType, Error>, UseQueryOmissions>
) => {
  const api = useApi()
  const result = useQuery<YoutubeStatusType, Error>({
    queryKey: ['customerYoutubeStatus', { customerGuid }],
    queryFn: async () => {
      try {
        const res = await api.get(`/customers/${customerGuid}/youtubestatus`)
        return res.status === 1 ? 'CONNECTED' : 'NOT_CONNECTED'
      } catch (err) {
        return 'INVALID_CONNECTION'
      }
    },
    staleTime: Infinity,
    enabled: !!customerGuid,
    ...props,
  })
  const status: YoutubeStatusType = result?.data ?? 'NOT_CONNECTED'
  const finalResult = { ...result, data: { status, isConnected: status === 'CONNECTED' } }
  return finalResult
}
